import classNames from 'clsx';
import { ReactComponent as IconClose } from 'img/icons/close_s__2.svg';
import React, { type ReactElement, memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BillingActions } from 'reactApp/modules/billing/billing.module';
import { BillingSelectors } from 'reactApp/modules/billing/billing.selectors';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import { PaymentWindowType } from 'reactApp/modules/payment/payment.analytics';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import type { RootState } from 'reactApp/store';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { DMR } from 'reactApp/ui/DMRFrame/DMR';
import { PaymentFooter } from 'reactApp/ui/PaymentFooter/PaymentFooter';
import { usePaymentInfo } from 'reactApp/ui/RenewSubscriptionModal/usePaymentInfo';
import { createGaSender } from 'reactApp/utils/ga';

import styles from './RenewSubscriptionModal.css';

interface Props {
    subscriptionId: string;
    onClose?: () => void;
    onSuccess?: () => void;
    isMobile?: boolean;
    isGrace?: boolean;
}

const sendBillingGa = createGaSender('billing');

export const RenewSubscriptionModal = memo(({ subscriptionId, onClose, onSuccess, isMobile, isGrace }: Props): ReactElement | null => {
    const dispatch = useDispatch();

    const subscription = useSelector((state: RootState) => BillingDataSelectors.getSubscriptionsById(state, subscriptionId));
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, subscription?.productId));

    const renewLink = useSelector(BillingSelectors.getPaymentRenewLink);
    const { isLoading, hasError } = useSelector(BillingSelectors.getLoadingState);

    const [isSuccess, setIsSuccess] = useState<boolean>(false);

    const { title, description } = usePaymentInfo({ isSuccess, subscription, isGrace, product });

    const renewSubscriptions = useCallback((): void => {
        dispatch(BillingActions.renewSubscription({ id: subscriptionId, isMobile, description, isMidas: true }));
    }, [dispatch, subscriptionId, description, isMobile]);

    const resetLink = useCallback((): void => {
        dispatch(BillingActions.resetLink());
    }, [dispatch]);

    const handleOnRenewClose = useCallback((): void => {
        sendBillingGa('renew-close');
        dispatch(BillingActions.resetLink());
        onClose?.();
    }, [dispatch, onClose]);

    const handleOnSuccessRenew = useCallback((): void => {
        sendBillingGa('renew-success');

        setIsSuccess(true);
        onSuccess?.();
    }, [onSuccess]);

    const handleOnErrorRenew = useCallback((): void => {
        sendBillingGa('renew-error');
    }, []);

    useEffect(() => {
        sendBillingGa('renew-show');
    }, []);

    if (!subscription) {
        return null;
    }

    return (
        <Dialog
            id="tariff-renew"
            open
            size="larger"
            header={isMobile || !title ? '' : title}
            onCancel={handleOnRenewClose}
            mod={isMobile ? 'mobile' : 'frame'}
            closeOnDimmerClick={false}
            closeOnEscape={false}
            dimmer={!isMobile}
            showCloseIcon={!isMobile}
            disableScrollOnBody
            closable
            disableDarkTheme
            className={classNames(styles.paymentModal, {
                [styles.mobileModal]: isMobile,
            })}
        >
            <div
                className={classNames(styles.root, {
                    [styles.root_mobile]: isMobile,
                })}
            >
                {isMobile && (
                    <div className={styles.headerBlock}>
                        {title && <div className={styles.header}>{title}</div>}
                        <div className={styles.closeIcon} onClick={handleOnRenewClose}>
                            <IconClose />
                        </div>
                    </div>
                )}
                <div className={classNames(styles.dmrContainer)}>
                    <DMR
                        buySubscription={renewSubscriptions}
                        resetSubscription={resetLink}
                        subscription={product}
                        onCancel={handleOnRenewClose}
                        onSuccess={handleOnSuccessRenew}
                        isLoading={isLoading}
                        hasError={hasError}
                        onError={handleOnErrorRenew}
                        checkoutLink={renewLink}
                        className={styles.dmr}
                        disableLoaderCentering
                        eventCategory="midasRenewBuyModal"
                        size={isMobile ? 'medium' : 'larger'}
                        skin="vkpay"
                        paySource="renew"
                        windowType={PaymentWindowType.renew}
                        isMidas
                        showCloseIcon={!isMobile}
                    />
                </div>
                <div className={styles.footer}>
                    <PaymentFooter productId={subscription?.productId} subscription={subscription} isMobile={isMobile} product={product} />
                </div>
            </div>
        </Dialog>
    );
});

RenewSubscriptionModal.displayName = 'RenewSubscriptionModal';
